import React, { useEffect, useRef, useState } from "react";

/**
 * Is some other modal dialog open on the page?
 *
 * The prompt renders inside the editor container, while dialogs (the Notion
 * search palette, our own) are portaled to `<body>`. The two therefore only meet
 * in the root stacking context, where this prompt's z-index competes with
 * whatever z-index the dialog happens to carry — and behind a dialog's overlay
 * it is dead UI regardless of who wins. So the prompt steps aside for as long as
 * a dialog is up, and comes back when it closes. Observed from the DOM because
 * most of those dialogs own their open state privately.
 */
const useOtherDialogOpen = (self: React.RefObject<HTMLElement>): boolean => {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsOpen(
        Array.from(
          document.querySelectorAll('[role="dialog"], [role="alertdialog"]'),
        ).some((node) => node !== self.current),
      );
    check();
    // Coalesced to one check per frame. The observer watches the whole body
    // subtree, and the canvas re-renders its integration overlay constantly, so
    // this fired thousands of times more often than the handful of dialog
    // open/close events it actually cares about — each one a document-wide
    // querySelectorAll.
    let queued = 0;
    const observer = new MutationObserver(() => {
      if (queued) {
        return;
      }
      queued = requestAnimationFrame(() => {
        queued = 0;
        check();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (queued) {
        cancelAnimationFrame(queued);
      }
    };
  }, [self]);
  return isOpen;
};

/**
 * Non-blocking handoff shown after sign-in when the anonymous scratch canvas
 * has content and no cloud file is open: offers to save that work to My Files.
 * Accepting runs the existing Save-to-My-Files flow; declining leaves the
 * scratch canvas local and is dismissed for the session (no nagging on mount).
 */
export const SaveWorkPrompt: React.FC<{
  onAccept: () => void;
  onDismiss: () => void;
}> = ({ onAccept, onDismiss }) => {
  const ref = useRef<HTMLDivElement>(null);
  const yieldToDialog = useOtherDialogOpen(ref);
  if (yieldToDialog) {
    return null;
  }
  return (
    <div
      ref={ref}
      data-testid="save-work-prompt"
      role="dialog"
      aria-label="Save your work"
      style={{
        position: "absolute",
        bottom: "1.25rem",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 6,
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.6rem 0.9rem",
        borderRadius: "0.6rem",
        background: "var(--island-bg-color, #fff)",
        boxShadow: "var(--shadow-island, 0 2px 10px rgba(0,0,0,0.18))",
        fontSize: "0.85rem",
      }}
    >
      <span>Save your work to My Files?</span>
      <button
        type="button"
        onClick={onAccept}
        style={{
          background: "#6965db",
          color: "#fff",
          border: "none",
          borderRadius: "0.375rem",
          padding: "0.35rem 0.75rem",
          fontSize: "0.8rem",
          cursor: "pointer",
        }}
      >
        Save
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent",
          border: "none",
          color: "#6b7280",
          cursor: "pointer",
          fontSize: "0.8rem",
        }}
      >
        Not now
      </button>
    </div>
  );
};

export default SaveWorkPrompt;
